import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines } from '@fortawesome/free-solid-svg-icons';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import FormFieldContext from '../FormFieldContext';
import { FieldData, SchedulingAppointmentTextFieldType } from 'types/FieldTypes';
import {
    AppointmentTextOption, APPOINTMENT_TEXT_OPTIONS, APPOINTMENT_TEXT_SEPARATOR, resolveAppointmentTextOptions
} from '../../../views/scheduling/functions/appointmentText';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import '../../../style/scss/forms.scss';

/*
 * SchedulingAppointmentTextField.tsx
 * Settings field in which the job fields shown in a scheduled
 * appointment block are chosen. The chosen fields are listed
 * first in their display order and can be dragged to reorder,
 * the other fields follow below. A preview block on the
 * right shows the result. Every change is saved right away like the other
 * settings fields.
 */

const SchedulingAppointmentTextField: React.FC<SchedulingAppointmentTextFieldType & { data: FieldData, viewKey: string }> = ({
    name, data, disabled = false
}) => {
    const { t } = useTranslation();
    const { updatedData, setUpdatedData, handleSubmit } = useContext(FormFieldContext);
    const [selectedOptions, setSelectedOptions] = useState<AppointmentTextOption[]>(resolveAppointmentTextOptions(null));

    // Set the selected options from the received data
    useEffect(() => {
        if (data) {
            setSelectedOptions(resolveAppointmentTextOptions(data[name]));
        }
    }, [data, name]);

    // The options which are not shown, in the fixed option order
    const availableOptions = useMemo(() => (
        APPOINTMENT_TEXT_OPTIONS.filter(option => !selectedOptions.includes(option.value))
    ), [selectedOptions]);

    // Resolves the option definition of an option key
    const getOption = (value: AppointmentTextOption) => APPOINTMENT_TEXT_OPTIONS.find(option => option.value === value);

    // Stores the new options and saves them right away
    const storeOptions = (options: AppointmentTextOption[]) => {
        setSelectedOptions(options);
        setUpdatedData({ ...updatedData, [name]: options });
        if (handleSubmit) {
            handleSubmit({ [name]: options });
        }
    };

    // Appends an option to the end of the shown fields
    const handleAddOption = (value: AppointmentTextOption) => {
        if (disabled) return;
        storeOptions([...selectedOptions, value]);
    };

    // Removes an option from the shown fields, the last one always stays
    const handleRemoveOption = (value: AppointmentTextOption) => {
        if (disabled || selectedOptions.length <= 1) return;
        storeOptions(selectedOptions.filter(option => option !== value));
    };

    // Stores the new order after a drag
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return;

        // Move the dragged option to its new position
        const reordered = Array.from(selectedOptions);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        storeOptions(reordered);
    };

    // The preview text built from the examples of the shown fields
    const previewText = selectedOptions
        .map(value => getOption(value)?.example)
        .filter(Boolean)
        .map(example => t(example as string))
        .join(APPOINTMENT_TEXT_SEPARATOR);

    // Renders the checkbox and label of an option row
    const renderOptionCheckbox = (value: AppointmentTextOption, checked: boolean, onToggle: () => void) => {
        const option = getOption(value);
        const checkboxId = `${name}_${value}`;
        const isLastSelected = checked && selectedOptions.length <= 1;

        return (
            <>
                <div className='checkbox-wrapper'>
                    <input
                        type='checkbox'
                        id={checkboxId}
                        checked={checked}
                        onChange={onToggle}
                        disabled={disabled || isLastSelected}
                        className='hidden-checkbox'
                    />
                    <label htmlFor={checkboxId} className={`custom-checkbox ${checked ? 'checked' : ''}`}>
                        {checked && (
                            <img src={CheckboxCheck} alt='CheckboxCheck' className='checkbox-check' />
                        )}
                    </label>
                </div>
                <label htmlFor={checkboxId} className='appointment-text-row-label'>
                    {option ? t(option.label) : value}
                </label>
            </>
        );
    };

    return (
        <div className={`appointment-text-field ${disabled ? 'disabled' : ''}`}>
            <div className='appointment-text-options'>
                <div className='appointment-text-caption'>
                    {t('scheduling.settings.appointment_text_selected_label')}
                </div>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <StrictModeDroppable droppableId={`${name}-selected`}>
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className='appointment-text-selected'>
                                {selectedOptions.map((value, index) => (
                                    <Draggable key={value} draggableId={value} index={index} isDragDisabled={disabled}>
                                        {(dragProvided, snapshot) => (
                                            <div
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                className={`appointment-text-row ${snapshot.isDragging ? 'dragging' : ''}`}
                                            >
                                                <div className='order-icon tooltip-icon' {...dragProvided.dragHandleProps}>
                                                    <FontAwesomeIcon icon={faGripLines} />
                                                    <span className='tooltip'>{t('general.reorder')}</span>
                                                </div>
                                                {renderOptionCheckbox(value, true, () => handleRemoveOption(value))}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </StrictModeDroppable>
                </DragDropContext>

                {availableOptions.length > 0 && (
                    <>
                        <div className='appointment-text-caption'>
                            {t('scheduling.settings.appointment_text_available_label')}
                        </div>
                        <div className='appointment-text-available'>
                            {availableOptions.map(option => (
                                <div key={option.value} className='appointment-text-row'>
                                    <div className='order-placeholder' />
                                    {renderOptionCheckbox(option.value, false, () => handleAddOption(option.value))}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className='appointment-text-preview'>
                <span className='event-start-time'>08:00</span>
                {previewText && (
                    <span className='event-text'>{APPOINTMENT_TEXT_SEPARATOR}{previewText}</span>
                )}
            </div>
        </div>
    );
};

export default SchedulingAppointmentTextField;
